(() => {
  'use strict';
  const root = document.getElementById('hsu-root');
  if (!root || window.HestanSetUpsell) return;
  const config = JSON.parse(document.getElementById('hsu-config').textContent);
  const offers = config.offers.sort((a, b) => b.priority - a.priority);
  const relevant = new Set(offers.flatMap(o => [...o.variants, ...o.existing, o.target]).map(Number));
  const sourceSets = new Set(offers.flatMap(o => o.existing).map(Number));
  const terminalSets = new Set(offers.map(o => Number(o.target)).filter(id => !sourceSets.has(id)));
  const html = document.documentElement;
  const base = window.Shopify?.routes?.root || '/';
  const originalFetch = window.fetch.bind(window);
  const seen = new Set();
  let active = null;
  let busy = false;
  let pending = 0;
  let serial = Promise.resolve();
  let previous = null;
  let previousFocus = null;
  let safetyTimer;
  let rebuyBlocked = false;
  const preview = root.dataset.preview === 'true' && new URLSearchParams(location.search).get('set_upsell_preview');
  const tracked = item => relevant.has(Number(item.variant_id || item.id));
  const fingerprint = cart => JSON.stringify(cart.items.filter(tracked).map(i => [Number(i.variant_id || i.id), i.quantity, i.properties, i.selling_plan_allocation?.selling_plan?.id]).sort());
  const plain = item => !item.selling_plan_allocation && !item.parent_relationship && !item.item_components?.length && Object.keys(item.properties || {}).every(key => key === '_hestan_set_upgrade') && item.quantity === 1 && item.final_price > 0;
  function match(cart) {
    if (!cart?.items || cart.items.some(i => terminalSets.has(Number(i.variant_id || i.id)))) return null;
    const items = cart.items.filter(tracked);
    if (!items.length || items.some(i => !plain(i))) return null;
    if (new Set(items.map(i => Number(i.variant_id || i.id))).size !== items.length) return null;
    for (const offer of offers) {
      if (!offer.available || cart.items.some(i => Number(i.variant_id || i.id) === Number(offer.target))) continue;
      const source = items.filter(i => offer.existing.includes(Number(i.variant_id || i.id)));
      const individual = items.filter(i => offer.variants.includes(Number(i.variant_id || i.id)));
      const contained = source.length + individual.length === items.length;
      if (!contained) continue;
      if (source.length === 1 && individual.length === 0 || source.length === 0 && individual.length === Number(offer.count)) return { offer, items };
    }
    return null;
  }
  function hasSeen(handle) {
    try { return seen.has(handle) || sessionStorage.getItem('hsu-seen-' + handle) === '1'; } catch { return seen.has(handle); }
  }
  function markSeen(handle) {
    seen.add(handle);
    try { sessionStorage.setItem('hsu-seen-' + handle, '1'); } catch {}
  }
  function track(event, offer, extra = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'set_upsell_' + event, offer: offer.handle, target_variant: offer.target, ...extra });
  }
  async function request(path, body) {
    const response = await originalFetch(base + path, { credentials: 'same-origin', cache: 'no-store', ...(body ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {}) });
    if (!response.ok) throw new Error('Cart request failed: ' + response.status);
    return response.json();
  }
  const readCart = () => request('cart.js');
  const initial = readCart().then(cart => { previous = cart; return cart; }).catch(() => null);
  function blockRebuy() {
    rebuyBlocked = true;
    html.classList.add('hsu-rebuy-blocked');
    hideRebuyPopup();
  }
  function suppress() {
    clearTimeout(safetyTimer);
    html.classList.add('hsu-suppress');
    safetyTimer = setTimeout(() => { if (!active && !busy) html.classList.remove('hsu-suppress'); }, 12000);
  }
  function hideRebuyPopup(candidate) {
    const widgets = candidate ? [candidate] : Object.values(window.Rebuy?.widgets || {});
    widgets.forEach(widget => {
      if (Number(widget?.id) !== 248994 || typeof widget.hide !== 'function') return;
      try { widget.hide(); } catch {}
    });
  }
  function release() {
    clearTimeout(safetyTimer);
    html.classList.remove('hsu-suppress');
  }
  function open(result, cart, visualOnly = false) {
    if (active || !result) return;
    const dialog = document.getElementById('hsu-' + result.offer.handle);
    if (!dialog) return;
    suppress();
    blockRebuy();
    active = { ...result, dialog, fingerprint: fingerprint(cart), visualOnly };
    previousFocus = document.activeElement;
    dialog.querySelector('.hsu__status').textContent = '';
    dialog.querySelector('[data-hsu-accept]').disabled = false;
    dialog.querySelector('[data-hsu-accept]').setAttribute('aria-disabled', String(visualOnly));
    dialog.showModal();
    html.classList.add('hsu-open');
    if (!visualOnly) { markSeen(result.offer.handle); track('impression', result.offer); }
    document.dispatchEvent(new CustomEvent('hestan:set-upsell:open', { detail: { handle: result.offer.handle } }));
  }
  function close(reason) {
    if (!active || busy) return;
    const state = active;
    if (!state.visualOnly) track(reason, state.offer);
    active = null;
    state.dialog.close();
    html.classList.remove('hsu-open');
    hideRebuyPopup();
    setTimeout(() => { if (!active && !pending) release(); }, 600);
    previousFocus?.focus?.({ preventScroll: true });
  }
  async function afterAdd() {
    try {
      await initial;
      const cart = await readCart();
      const changed = !previous || fingerprint(cart) !== fingerprint(previous);
      previous = cart;
      const eligible = match(cart);
      if (eligible) blockRebuy();
      else if (!active && !busy && changed) {
        rebuyBlocked = false;
        html.classList.remove('hsu-rebuy-blocked');
      }
      const result = changed ? eligible : null;
      const staging = root.dataset.preview === 'true';
      if (!busy && result && (staging || !hasSeen(result.offer.handle))) open(result, cart);
      else if (!active && !busy) release();
    } catch { if (!active && !busy) release(); }
  }
  function isAdd(url, method) {
    try { const parsed = new URL(url, location.href); return parsed.origin === location.origin && /\/cart\/add(?:\.js)?\/?$/.test(parsed.pathname) && method.toUpperCase() === 'POST'; } catch { return false; }
  }
  function beginAdd() { pending++; if (!busy) suppress(); }
  function endAdd(success) {
    pending = Math.max(0, pending - 1);
    if (busy) return;
    if (success) serial = serial.then(afterAdd, afterAdd);
    else if (!pending && !active) release();
  }
  window.fetch = function(input, init) {
    const add = isAdd(typeof input === 'string' || input instanceof URL ? String(input) : input.url, init?.method || input?.method || 'GET');
    if (add) beginAdd();
    return originalFetch(input, init).then(response => { if (add) endAdd(response.ok); return response; }, error => { if (add) endAdd(false); throw error; });
  };
  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function(method, url, ...args) { this.hsuAdd = isAdd(String(url), method); return xhrOpen.call(this, method, url, ...args); };
  XMLHttpRequest.prototype.send = function(...args) {
    if (this.hsuAdd) { beginAdd(); this.addEventListener('loadend', () => endAdd(this.status >= 200 && this.status < 300), { once: true }); }
    return xhrSend.apply(this, args);
  };
  document.addEventListener('rebuy:cart.add', () => {
    if (busy || pending) return;
    suppress();
    serial = serial.then(afterAdd, afterAdd);
  });
  document.addEventListener('rebuy.show', event => {
    if (rebuyBlocked || active || busy) hideRebuyPopup(event.detail?.widget);
  });
  document.addEventListener('rebuy.beforeShow', event => {
    if (!(rebuyBlocked || active || busy)) return;
    const widget = event.detail?.widget;
    if (Number(widget?.id) === 248994) setTimeout(() => hideRebuyPopup(widget), 0);
  });
  async function refreshCart(cart) {
    cart = cart || await readCart();
    const rebuy = window.Rebuy;
    if (rebuy?.Cart?.setCart) rebuy.Cart.setCart(cart);
    if (rebuy?.SmartCart?.setCart) rebuy.SmartCart.setCart(cart);
    rebuy?.SmartCart?.updateCartCount?.();
    rebuy?.SmartCart?.updateCartSubtotal?.();
    document.dispatchEvent(new CustomEvent('cart:build'));
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }
  async function accept() {
    if (!active || busy) return;
    const state = active;
    const status = state.dialog.querySelector('.hsu__status');
    const button = state.dialog.querySelector('[data-hsu-accept]');
    busy = true;
    button.disabled = true;
    state.dialog.querySelectorAll('[data-hsu-dismiss]').forEach(b => b.disabled = true);
    status.textContent = config.messages.busy;
    let attempted = false;
    let committed = false;
    const marker = 'hsu-' + Date.now() + '-' + Math.random().toString(36).slice(2);
    try {
      const cart = await readCart();
      const current = match(cart);
      if (!current || current.offer.handle !== state.offer.handle || !state.visualOnly && fingerprint(cart) !== state.fingerprint) throw new Error('changed');
      if (state.visualOnly) {
        state.items = current.items;
        state.fingerprint = fingerprint(cart);
        state.visualOnly = false;
        button.setAttribute('aria-disabled', 'false');
      }
      const product = await request('products/' + state.offer.productHandle + '.js');
      if (!product.variants.some(v => Number(v.id) === Number(state.offer.target) && v.available)) throw new Error('changed');
      track('click', state.offer);
      attempted = true;
      await request('cart/add.js', { items: [{ id: state.offer.target, quantity: 1, properties: { _hestan_set_upgrade: marker } }] });
      const after = await readCart();
      const removals = {};
      for (const original of current.items) {
        const candidates = after.items.filter(i => Number(i.variant_id) === Number(original.variant_id) && JSON.stringify(i.properties || {}) === JSON.stringify(original.properties || {}) && i.quantity === original.quantity);
        if (candidates.length !== 1) throw new Error('changed');
        removals[candidates[0].key] = 0;
      }
      await request('cart/update.js', { updates: removals });
      const final = await readCart();
      if (!final.items.some(i => i.properties?._hestan_set_upgrade === marker && i.quantity === 1) || current.items.some(o => final.items.some(i => Number(i.variant_id) === Number(o.variant_id)))) throw new Error('verify');
      committed = true;
      previous = final;
      await refreshCart(final);
      track('success', state.offer);
      busy = false;
      close('accepted');
      if (window.Rebuy?.SmartCart?.show) window.Rebuy.SmartCart.show();
      else document.dispatchEvent(new CustomEvent('cart:open'));
    } catch (error) {
      if (committed) {
        busy = false;
        close('accepted');
        status.textContent = config.messages.review;
        document.dispatchEvent(new CustomEvent('cart:open'));
        return;
      }
      let recovered = !attempted;
      if (attempted) {
        try {
          let cart = await readCart();
          let set = cart.items.find(i => i.properties?._hestan_set_upgrade === marker);
          if (set) {
            const missing = state.items.filter(o => !cart.items.some(i => Number(i.variant_id) === Number(o.variant_id)));
            if (missing.length) await request('cart/add.js', { items: missing.map(i => ({ id: i.variant_id, quantity: i.quantity, properties: i.properties || {} })) });
            cart = await readCart();
            const remaining = state.items.every(o => cart.items.some(i => Number(i.variant_id) === Number(o.variant_id) && i.quantity === o.quantity));
            set = cart.items.find(i => i.properties?._hestan_set_upgrade === marker);
            if (remaining && set) await request('cart/change.js', { id: set.key, quantity: 0 });
          }
          cart = await readCart();
          recovered = !cart.items.some(i => i.properties?._hestan_set_upgrade === marker) && state.items.every(o => cart.items.some(i => Number(i.variant_id) === Number(o.variant_id) && i.quantity === o.quantity));
        } catch {}
      }
      track('error', state.offer, { reason: error.message, recovered });
      status.textContent = !recovered ? config.messages.review : error.message === 'changed' ? config.messages.changed : config.messages.error;
      if (!recovered) {
        const link = document.createElement('a'); link.href = base + 'cart'; link.textContent = config.messages.review; status.append(' ', link);
      }
      await refreshCart().catch(() => {});
      busy = false;
      button.disabled = true;
      state.dialog.querySelectorAll('[data-hsu-dismiss]').forEach(b => b.disabled = false);
    }
  }
  root.addEventListener('click', event => {
    if (event.target.closest('[data-hsu-dismiss]')) close('dismiss');
    if (event.target.closest('[data-hsu-accept]')) accept();
    if (event.target.tagName === 'DIALOG') { const r = event.target.getBoundingClientRect(); if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) close('dismiss'); }
  });
  root.querySelectorAll('dialog').forEach(d => d.addEventListener('cancel', event => { event.preventDefault(); close('dismiss'); }));
  window.HestanSetUpsell = { match, version: '1.2.1' };
  if (preview) {
    const offer = offers.find(o => o.handle === preview || o.handle === 'nanobond-' + preview + '-piece');
    if (offer) open({ offer, items: [] }, { items: [] }, true);
  }
})();
